/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { mean } from 'lodash'
import { ethers } from 'ethers'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'

import {
  DEFAULT_DECIMALS,
  getERCAssetAddress,
  PoolAsset,
} from 'modules/pools/constants/poolAssets'
import { POOL_INVESTORS_COLLECTION } from 'modules/pools/utils/getPoolInvestorContract'
import {
  ContractPriceFeed,
  ContractRevenueManager,
  ContractScoreDB,
} from 'modules/contracts/contracts'
import { PoolRisk } from 'modules/pools/constants/PoolRisk'
import { weiToNum } from 'modules/blockChain/utils/parseWei'
import { getCollateralRangeFromLTVs } from './utils'
import { getNFCSScoresByPoolRisk } from 'modules/nfcs/utils/NFCSScores'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'
import { getPoolProjectedApy } from 'modules/pools/utils/getPoolProjectedApy'

const INITIAL_DATA = [PoolAsset.USDC] as const

export function useMarketsData({ poolRisk }: { poolRisk: PoolRisk }) {
  const { chainId } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const richedData = useSWR(`markets-${chainId}-${poolRisk}`, async () => {
    const contractScoreDB = ContractScoreDB.connectRpc({ chainId })
    const revenueManager = ContractRevenueManager.connectRpc({
      chainId,
    })
    const priceFeed = ContractPriceFeed.connectRpc({ chainId })

    const requests = INITIAL_DATA.map(async asset => {
      const poolInvestor = POOL_INVESTORS_COLLECTION.find(
        i => i.asset === asset && i.risk === poolRisk,
      )!

      const assetDecimals = getAssetDecimals(poolInvestor.asset)

      const NFCSScores = getNFCSScoresByPoolRisk(poolRisk)
      const assetAddress = getERCAssetAddress(poolInvestor.asset, chainId)

      const poolInvestorContract = poolInvestor.contract.connectRpc({ chainId })

      const ltvRequests = Promise.all<ethers.BigNumber>(
        NFCSScores.map(nfcs => contractScoreDB.LTV(assetAddress, nfcs)),
      )

      const [
        totalDeposit,
        apr,
        [tokenPrice, tokenPriceDecimals],
        ltvsWei,
        balanceAvailable,
        apy,
      ] = await Promise.all([
        poolInvestorContract.poolValue(),
        poolInvestorContract.interestRateAnnual(),
        priceFeed.getLatestPriceUSD(assetAddress),
        ltvRequests,
        revenueManager.balanceAvailable(poolInvestorContract.address),
        getPoolProjectedApy({
          poolInvestor: poolInvestorContract,
          chainId,
        }),
      ])

      const tokenPriceNum = weiToNum(tokenPrice, tokenPriceDecimals)

      const depositMinusBalance = totalDeposit.sub(balanceAvailable)

      const totalBorrowedAmount =
        weiToNum(
          depositMinusBalance.isNegative() ? 0 : depositMinusBalance,
          assetDecimals,
        ) * tokenPriceNum

      const collateralRange = getCollateralRangeFromLTVs(
        ltvsWei.map(ltv => weiToNum(ltv, DEFAULT_DECIMALS)),
      )

      const totalDepositUsd =
        weiToNum(totalDeposit, assetDecimals) * tokenPriceNum

      const borrowInterest = apr.div(365).mul(30)

      return {
        totalDeposit: totalDepositUsd,
        asset,
        borrowInterest: weiToNum(borrowInterest, DEFAULT_DECIMALS),
        depositAPY: apy,
        borrowAPR: weiToNum(apr, DEFAULT_DECIMALS),
        collateralRange: collateralRange,
        collateralMean: mean(collateralRange),
        totalBorrowedAmount,
      }
    })

    const res = await Promise.all(requests)

    return res
  })

  return richedData
}
